import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import type {
  CreateDirectoriesBatchRequest,
  CreateDirectoryRequest,
  EditDirectoryRequest,
} from 'shared/domain/messages/directory/eventMessages';
import { DirectoryEntity } from 'serviceWorker/repository/directory/entity';
import { directoryEntityToModel } from 'serviceWorker/repository/directory/mappings';
import {
  broadcastAllDirectories,
  broadcastDirectoriesBatchCreateFailed,
} from 'serviceWorker/broadcasts/directories';
import * as directories from 'serviceWorker/db/directories/directories';
import * as directoriesService from 'serviceWorker/db/directories/directoriesService';
import { debugLog } from 'shared/logger/debugLog';
import { getUsersSynchronized } from '../users/getUsers';
import { directorySaver } from './addDirectory';
import { directoriesBatchSaver } from './addDirectoriesBatch';
import { directoryEditor } from './editDirectory';
import {
  fetchDirectories,
  fetchUpdatedDirectories,
} from './fetchDirectories';
import { Services } from 'shared/domain/messages/message';
import { directoryContract } from 'shared/domain/directory/directoryContract';
import { createSyncedEntity } from '../factories/createSyncedEntity';

/** Download and sync of the directories; the source's `sync` is its entry point. */
export const directoriesSync = createSyncedEntity({
  entityName: Services.DIRECTORIES,
  contract: directoryContract,
  repository: { ...directories, addBatch: directories.fastAddBatch },
  service: directoriesService,
  fetch: fetchDirectories,
  fetchUpdated: fetchUpdatedDirectories,
  publishCollection: broadcastAllDirectories,
});

export async function getDirectory(
  localId: number
): Promise<DirectoryModel> {
  const [directory, users] = await Promise.all([
    directories.getOne(localId),
    getUsersSynchronized(),
  ]);
  if (!directory) {
    throw new Error(`Cannot find directory with localId ${localId}`);
  }
  return directoryEntityToModel(directory, users);
}

export async function getDirectories(
  ids: number[]
): Promise<DirectoryEntity[]> {
  return directories.getByIds(ids);
}

export function handleCreateDirectory(
  detail: CreateDirectoryRequest
): void {
  debugLog('addDirectory', detail);
  directorySaver.execute(detail.directory, detail.uniqueId);
}

export async function handleCreateDirectoriesBatch(
  detail: CreateDirectoriesBatchRequest
): Promise<void> {
  debugLog('addDirectoriesBatch', detail);
  try {
    await directoriesBatchSaver.execute(
      detail.directories,
      detail.uniqueId
    );
  } catch (error) {
    broadcastDirectoriesBatchCreateFailed(error, detail.uniqueId);
  }
}

export function handleEditDirectory(
  detail: EditDirectoryRequest
): void {
  debugLog('editDirectory', detail);
  directoryEditor.execute(detail.directory, detail.uniqueId);
}
